<template>
  <UIModal
    :model-value="showConsent"
    persistent
    data-test-id="cookie-consent-modal"
  >
    <h2 class="mt-2 flex items-center gap-4 font-semibold">
      <UIIcon>
        <MaterialSymbolsCookieOutlineRounded />
      </UIIcon>
      {{ $t('cookie.title') }}
    </h2>

    <form @submit.prevent="onSubmit">
      <div
        class="mb-2 mt-5 flex items-center justify-between gap-3"
        data-test-id="necessary-cookie-switch"
      >
        <label class="flex items-center gap-3">
          <UICheckbox v-model="necessary" disabled />
          <span class="text-min font-medium">
            {{ $t('cookie.necessary') }}
          </span>
        </label>
      </div>

      <div
        class="mb-4 flex items-center justify-between gap-3"
        data-test-id="marketing-cookie-switch"
      >
        <label class="flex items-center gap-3">
          <UICheckbox v-model="marketing" />

          <span class="text-min font-medium">
            {{ $t('cookie.marketing') }}
          </span>
        </label>
      </div>

      <i18n-t
        v-if="cookiePolicyUrl"
        keypath="cookie.description"
        scope="global"
        tag="p"
        class="my-4"
      >
        <template #link>
          <UIExternalLink :to="cookiePolicyUrl">
            {{ $t('cookie.linkText') }}
          </UIExternalLink>
        </template>
      </i18n-t>

      <div class="mt-6 flex gap-4 text-center">
        <UIButton
          data-test-id="submit-button"
          type="button"
          variant="secondary"
          responsive
          @click="handleSaveSelected"
        >
          {{ $t('cookie.saveSelected') }}
        </UIButton>
        <UIButton data-test-id="submit-button" type="submit" responsive>
          {{ $t('cookie.acceptAll') }}
        </UIButton>
      </div>
    </form>
  </UIModal>
</template>

<script lang="ts" setup>
import { useCookieConsent } from '@booking-portal/modules/cookie/composables/use-cookie-consent';
import UICheckbox from '@booking-portal/modules/form/components/UICheckbox.vue';
import UIButton from '@booking-portal/modules/ui/components/UIButton.vue';
import UIExternalLink from '@booking-portal/modules/ui/components/UIExternalLink.vue';
import UIIcon from '@booking-portal/modules/ui/components/UIIcon.vue';
import UIModal from '@booking-portal/modules/ui/components/UIModal.vue';
import { useAppStore } from '@booking-portal/stores/app-store';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();
const locationStore = useAppStore();
const {
  hasSeenCookieConsent,
  hasMarketingCookies,
  isSettingsForceShown,
  setCookies,
  hideSettings,
} = useCookieConsent();

// `necessary` is always required and the checkbox is disabled — we still
// track it as a ref so the form bindings stay explicit.
const necessary = ref(true);
const marketing = ref(false);

// Show the modal on the initial-consent path (no stored choice yet) OR when
// the footer's "change privacy settings" link flips the force flag.
const showConsent = computed(
  () => !hasSeenCookieConsent.value || isSettingsForceShown.value
);

// Seed the marketing toggle from stored consent every time the modal opens.
// For first-time users `hasMarketingCookies` is `false`, matching the old
// default. For returning users re-entering via the footer link, they see
// their previous choice pre-selected.
watch(
  showConsent,
  (isOpen) => {
    if (isOpen) {
      marketing.value = hasMarketingCookies.value;
    }
  },
  { immediate: true }
);

const isCustomerPortalRoute = computed(() =>
  route.fullPath?.includes('customer-portal')
);

const cookiePolicyUrl = computed(() =>
  isCustomerPortalRoute.value
    ? locationStore.globalPrivacyPolicyUrl
    : locationStore.cookiePolicyUrl
);

function handleSaveSelected() {
  setCookies({
    necessary: necessary.value,
    marketing: marketing.value,
  });
  hideSettings();
}

function onSubmit() {
  setCookies({
    necessary: true,
    marketing: true,
  });
  hideSettings();
}
</script>
